import Vue from 'vue';
import { arrayManagementService } from '../../../services/ArrayManagementService';
import {
  updatePosition,
  updateName,
  moveToNewString,
  bulkUpdate
} from '../../../apis/panel/module_api.js';
import {
  updateName as updateStringNameApi,
  moveToArray as moveStringToArrayAPI
} from '../../../apis/panel/string_api.js';
import siteDesignApi from '../../../apis/panel/site_design_api.js';
import {
  flattenSiteDesignData
} from '../../../utils/siteDesignDataFlattener.js';
import {
  resolveSolarStringSeqFromTreeItem,
  resolveSolarArraySeqFromTreeItem,
  resolveSolarModuleDeleteKeyFromTreeItem,
  moveModuleNodeToString
} from '../../../features/panel-planning/utils/flattenModuleTreeRows';
import {
  panelPerfMark,
  panelPerfMeasure,
  panelPerfCountSummary
} from '../../../features/panel-planning/utils/panelPlanningPerfLog.js';
import {
  clearPanelFlattenedRegistry,
  getPanelFlattenedRegistry,
  setPanelFlattenedRegistry
} from '../../../features/panel-planning/utils/panelFlattenedRegistry.js';
import {
  addArrayToPanelTree,
  addStringToPanelTree,
  collectPanelTreeArrayDisplayNumbers,
  findPanelTreeArrayIndexById,
  getPanelTreeLocalRegistry,
  hydrateStringTreeModulesInRegistry,
  moveStringInPanelTreeInPlace,
  removeArrayFromPanelTreeByIndex,
  removeArrayGroupFromPanelTree,
  removePanelTreeNodeById,
  renamePanelTreeNode,
  setPanelTreeLocalRegistry,
  updateArrayGroupInPanelTree,
  notifyPanelTreeLocalChange
} from '../../../features/panel-planning/utils/panelTreeLocalRegistry.js';
import i18n from '../../../plugins/i18n';
import {
  calculateMaxArrayDisplayIdFromFlattened,
  calculateMaxModuleIdInStringFromFlattened,
  calculateMaxStringIdInArrayFromFlattened,
  normalizeEntityRenameName,
  resolveMaxStringDisplayIdInArray,
  resolveNextArrayDisplayId,
  resolveNextModuleDisplayIdInString,
  resolveNextStringDisplayIdInArray
} from '../../../features/panel-planning/utils/panelEntityNumbering.js';
import {
  moduleDomainToTreeFields,
  moduleFromApi,
  readStringDisplayNoFromTreeItem,
  readStringSeqFromTreeItem,
  stringDomainToTreeFields,
  stringFromApi
} from '../../../features/panel-planning/utils/panelHierarchyMappers.js';
import { readStringSeq } from '../../../features/panel-planning/utils/panelMapFeatureProps.js';

/**
 * 🎯 패널 플래닝 페이지의 모든 데이터를 중앙 관리하는 Store
 * 서버 데이터, 평면화된 데이터, 가상 트리, 지도 데이터를 통합 관리
 *
 * === ID 규칙 (필수) ===
 * - selfSeq / seq:     DB seq (Long) — API call·Map key·객체 구분 기준
 * - displayId / moduleId / stringId: 화면 표시 번호 — 라벨·검색용
 * - mapKey:            "${type}_${seq}" — derivedMapData lookup key
 *
 * 비교 시 항상 selfSeq(DB seq)로 판단할 것. displayId로 절대 비교 금지.
 */
const state = {
  serverSiteDesignData: null, // 서버에서 받아온 사이트 설계 데이터
  selectedSiteData: null, // 현재 선택된 사이트 데이터

  // flattened canonical — panelFlattenedRegistry (Vuex 반응성 밖)
  // module tree canonical — panelTreeLocalRegistry (Vuex 반응성 밖)

  // 🎯 가상 트리 데이터 (TreeNode용, 새로 추가)
  virtualTree: null,

  // 🎯 기존 파생 데이터 (호환성 유지)
  derivedMapData: new Map(), // 지도 데이터 (서버 데이터 기반으로 자동 생성)

  // 🎯 지도 데이터 (새로 추가)
  mapData: new Map(),

  // 🎯 선택 상태 (통합)
  selectedEntities: new Set(),
  selectedNodeCollection: [], // 선택된 노드 목록
  editingNodeId: null, // 편집 중인 노드 ID
  isDraggingState: false, // 드래그 상태
  draggedNodeId: null, // 드래그 중인 노드 ID
  isEditing: false,
  pendingChanges: new Map(),

  // 🎯 낙관적 업데이트를 위한 임시 상태
  optimisticUpdates: {
    pendingChanges: new Map(), // 대기 중인 변경사항 (ID -> 변경사항)
    isOptimisticMode: false, // 낙관적 업데이트 모드 여부
    originalStates: new Map() // 원본 상태 저장 (롤백용)
  },

  // 🎯 동기화 상태 관리
  synchronizationState: {
    isSynchronizing: false, // 동기화 진행 중 여부
    pendingChanges: new Set(), // 대기 중인 변경사항
    lastSynchronizationTime: null, // 마지막 동기화 시간
    synchronizationErrors: [] // 동기화 오류 목록
  },

  // 🎯 작업 상태 관리
  isLoadingState: false, // 데이터 로딩 상태
  isCreatingState: false, // 생성 작업 상태
  isUpdatingState: false, // 수정 작업 상태
  isDeletingState: false, // 삭제 작업 상태
  isBulkProcessingState: false, // 대량 처리 상태

  // 🎯 에러/성공 상태 관리
  errorState: null, // 에러 상태
  errorMessage: '', // 에러 메시지
  successState: null, // 성공 상태
  successMessage: '' // 성공 메시지
};

const mutations = {
  // 🎯 서버 데이터 관련 mutations (단일 진실 소스)
  setServerSiteDesignData(state, siteDesignData) {
    state.serverSiteDesignData = siteDesignData;
    // 서버 데이터가 변경되면 파생 데이터도 자동으로 업데이트
    // mutations에서 직접 호출하지 않고, actions에서 처리
  },

  setSelectedSiteData(state, siteData) {
    state.selectedSiteData = siteData;
  },

  // 🎯 평면화된 데이터 관련 mutations
  setFlattenedData(state, flattenedData) {
    void state;
    if (!flattenedData) {
      clearPanelFlattenedRegistry();
      return;
    }
    if (getPanelFlattenedRegistry() === flattenedData) {
      panelPerfCountSummary('moduleTree:setFlattenedRegistry:skip', { reason: 'sameRef' });
      return;
    }
    panelPerfMark('moduleTree:setFlattenedRegistry:start');
    setPanelFlattenedRegistry(flattenedData);
    panelPerfMeasure('moduleTree:setFlattenedRegistry', 'moduleTree:setFlattenedRegistry:start', {
      rows: flattenedData.flattened?.length ?? 0,
      modules: flattenedData.byModule?.size ?? 0
    });
  },

  HYDRATE_STRING_TREE_MODULES(state, payload) {
    void state;
    hydrateStringTreeModulesInRegistry(payload?.stringNodeId, payload?.moduleNodes);
  },

  // 🎯 가상 트리 관련 mutations
  setVirtualTree(state, virtualTree) {
    state.virtualTree = virtualTree;
  },

  // 🎯 지도 데이터 관련 mutations
  setMapData(state, mapData) {
    state.mapData = mapData;
  },

  // 🎯 선택 상태 관련 mutations
  setSelectedEntities(state, selectedEntities) {
    state.selectedEntities = selectedEntities;
  },

  addSelectedEntity(state, entityId) {
    state.selectedEntities.add(entityId);
  },

  removeSelectedEntity(state, entityId) {
    state.selectedEntities.delete(entityId);
  },

  clearSelectedEntities(state) {
    state.selectedEntities.clear();
  },

  // 🎯 파생 데이터 관련 mutations
  setDerivedTreeData(state, treeData) {
    void state;
    setPanelTreeLocalRegistry(treeData);
  },

  setDerivedMapData(state, mapData) {
    state.derivedMapData = mapData;
  },

  // Tree 데이터 관련 mutations (파생 데이터 업데이트)
  updateArrayGroupInTree(state, payload) {
    void state;
    updateArrayGroupInPanelTree(payload?.arrayId, payload?.updatedData);
  },

  removeArrayGroupFromTree(state, arrayId) {
    void state;
    removeArrayGroupFromPanelTree(arrayId);
  },

  // 지도 데이터 관련 mutations (파생 데이터 업데이트)
  addFeatureToMap(state, { featureId, feature }) {
    state.derivedMapData.set(featureId, feature);
  },

  removeFeatureFromMap(state, featureId) {
    state.derivedMapData.delete(featureId);
  },

  updateFeatureInMap(state, { featureId, updatedFeature }) {
    if (state.derivedMapData.has(featureId)) {
      state.derivedMapData.set(featureId, updatedFeature);
    }
  },

  // 동기화 상태 관련 mutations
  setSynchronizationState(
    state,
    { isSynchronizing, pendingChanges, lastSynchronizationTime, synchronizationErrors }
  ) {
    if (isSynchronizing !== undefined) state.synchronizationState.isSynchronizing = isSynchronizing;
    if (pendingChanges !== undefined) state.synchronizationState.pendingChanges = pendingChanges;
    if (lastSynchronizationTime !== undefined)
      state.synchronizationState.lastSynchronizationTime = lastSynchronizationTime;
    if (synchronizationErrors !== undefined)
      state.synchronizationState.synchronizationErrors = synchronizationErrors;
  },

  addPendingChange(state, changeId) {
    state.synchronizationState.pendingChanges.add(changeId);
  },

  removePendingChange(state, changeId) {
    state.synchronizationState.pendingChanges.delete(changeId);
  },

  addSynchronizationError(state, error) {
    state.synchronizationState.synchronizationErrors.push(error);
  },

  clearSynchronizationErrors(state) {
    state.synchronizationState.synchronizationErrors = [];
  },

  // 🎯 선택/편집 상태 관련 mutations
  setSelectedNodeCollection(state, selectedNodes) {
    state.selectedNodeCollection = selectedNodes;
  },

  addSelectedNode(state, nodeId) {
    if (!state.selectedNodeCollection.includes(nodeId)) {
      state.selectedNodeCollection.push(nodeId);
    }
  },

  removeSelectedNode(state, nodeId) {
    const index = state.selectedNodeCollection.indexOf(nodeId);
    if (index !== -1) {
      state.selectedNodeCollection.splice(index, 1);
    }
  },

  clearSelectedNodes(state) {
    state.selectedNodeCollection = [];
  },

  setEditingNodeId(state, nodeId) {
    state.editingNodeId = nodeId;
  },

  setDraggingState(state, { isDragging, draggedNodeId }) {
    state.isDraggingState = isDragging;
    state.draggedNodeId = draggedNodeId;
  },

  // 🎯 작업 상태 관련 mutations
  setLoadingState(state, isLoading) {
    state.isLoadingState = isLoading;
  },

  setCreatingState(state, isCreating) {
    state.isCreatingState = isCreating;
  },

  setUpdatingState(state, isUpdating) {
    state.isUpdatingState = isUpdating;
  },

  setDeletingState(state, isDeleting) {
    state.isDeletingState = isDeleting;
  },

  setBulkProcessingState(state, isBulkProcessing) {
    state.isBulkProcessingState = isBulkProcessing;
  },

  // 🎯 에러/성공 상태 관련 mutations
  setErrorState(state, { error, message }) {
    state.errorState = error;
    state.errorMessage = message || '';
  },

  clearErrorState(state) {
    state.errorState = null;
    state.errorMessage = '';
  },

  setSuccessState(state, { success, message }) {
    state.successState = success;
    state.successMessage = message || '';
  },

  clearSuccessState(state) {
    state.successState = null;
    state.successMessage = '';
  },

  // 🎯 사용자 인터랙션 관련 mutations (낙관적 업데이트)
  updateModulePositionOptimistically(state, { moduleId, newPosition }) {
    // 1. 원본 상태 저장 (롤백용)
    const moduleFeature = state.derivedMapData.get(`module_${moduleId}`);
    if (moduleFeature && !state.optimisticUpdates.originalStates.has(`module_${moduleId}`)) {
      state.optimisticUpdates.originalStates.set(`module_${moduleId}`, {
        geometry: moduleFeature.geometry,
        type: 'position'
      });
    }

    // 2. 임시 변경사항 저장
    state.optimisticUpdates.pendingChanges.set(`module_${moduleId}`, {
      type: 'position',
      newValue: newPosition,
      timestamp: Date.now()
    });

    // 3. 낙관적 모드 활성화
    state.optimisticUpdates.isOptimisticMode = true;

    // 4. 지도 데이터 즉시 업데이트
    if (moduleFeature) {
      moduleFeature.geometry = newPosition;
      state.derivedMapData.set(`module_${moduleId}`, moduleFeature);
    }
  },

  // 🎯 모듈 이름 변경 (낙관적 업데이트)
  updateModuleNameOptimistically(state, { moduleId, newName, newSeq }) {
    // 1. 원본 상태 저장 (롤백용)
    const moduleFeature = state.derivedMapData.get(`module_${moduleId}`);
    if (moduleFeature && !state.optimisticUpdates.originalStates.has(`module_${moduleId}`)) {
      state.optimisticUpdates.originalStates.set(`module_${moduleId}`, {
        name: moduleFeature.properties.name,
        seq: moduleFeature.properties.seq,
        type: 'name'
      });
    }

    // 2. 임시 변경사항 저장
    state.optimisticUpdates.pendingChanges.set(`module_${moduleId}`, {
      type: 'name',
      newValue: { name: newName, seq: newSeq },
      timestamp: Date.now()
    });

    // 3. 낙관적 모드 활성화
    state.optimisticUpdates.isOptimisticMode = true;

    // 4. Tree 데이터 즉시 업데이트
    for (const arrayGroup of getPanelTreeLocalRegistry()) {
      for (const stringNode of arrayGroup.children) {
        const moduleNode = stringNode.children.find(module => module.solarModuleId === moduleId);
        if (moduleNode) {
          moduleNode.name = newName;
          moduleNode.seq = newSeq;
          break;
        }
      }
    }
    notifyPanelTreeLocalChange();

    // 5. 지도 데이터 즉시 업데이트
    if (moduleFeature) {
      moduleFeature.properties.name = newName;
      moduleFeature.properties.seq = newSeq;
      state.derivedMapData.set(`module_${moduleId}`, moduleFeature);
    }

    // 6. 가상 트리 업데이트
    if (state.virtualTree) {
      const walk = nodes => {
        for (const n of nodes) {
          if (n.id === moduleId && n.type === 'module') {
            n.name = newName;
            n.seq = newSeq;
            return true;
          }
          if (n.children && walk(n.children)) return true;
        }
        return false;
      };
      walk(state.virtualTree.children);
    }

    // 7. 평면화된 데이터 업데이트
    if (getPanelFlattenedRegistry()?.byModule) {
      const module = getPanelFlattenedRegistry().byModule.get(moduleId);
      if (module) {
        module.name = newName;
        module.seq = newSeq;
      }
    }
  },

  // 🎯 가상 트리에서 모듈 이름 업데이트
  updateModuleNameInVirtualTree(state, moduleId, newName, newSeq) {
    const updateNodeRecursively = nodes => {
      for (const node of nodes) {
        if (node.id === moduleId && node.type === 'module') {
          node.name = newName;
          node.seq = newSeq;
          return true;
        }
        if (node.children && updateNodeRecursively(node.children)) {
          return true;
        }
      }
      return false;
    };

    updateNodeRecursively(state.virtualTree.children);
  },

  // 🎯 String 이름 변경 (낙관적 업데이트)
  updateStringNameOptimistically(state, { stringId, newName, newSeq }) {
    // 1. 원본 상태 저장 (롤백용)
    const stringFeature = state.derivedMapData.get(`string_${stringId}`);
    if (stringFeature && !state.optimisticUpdates.originalStates.has(`string_${stringId}`)) {
      state.optimisticUpdates.originalStates.set(`string_${stringId}`, {
        name: stringFeature.properties.name,
        seq: stringFeature.properties.seq,
        type: 'name'
      });
    }

    // 2. 임시 변경사항 저장
    state.optimisticUpdates.pendingChanges.set(`string_${stringId}`, {
      type: 'name',
      newValue: { name: newName, seq: newSeq },
      timestamp: Date.now()
    });

    // 3. 낙관적 모드 활성화
    state.optimisticUpdates.isOptimisticMode = true;

    // 4. Tree 데이터 즉시 업데이트
    for (const arrayGroup of getPanelTreeLocalRegistry()) {
      const stringNode = arrayGroup.children.find(string => string.solarStringId === stringId);
      if (stringNode) {
        stringNode.name = newName;
        stringNode.seq = newSeq;
        break;
      }
    }
    notifyPanelTreeLocalChange();

    // 5. 지도 데이터 즉시 업데이트
    if (stringFeature) {
      stringFeature.properties.name = newName;
      stringFeature.properties.seq = newSeq;
      state.derivedMapData.set(`string_${stringId}`, stringFeature);
    }

    // 6. 가상 트리 업데이트
    if (state.virtualTree) {
      const walk = nodes => {
        for (const n of nodes) {
          if (n.id === stringId && n.type === 'string') {
            n.name = newName;
            n.seq = newSeq;
            return true;
          }
          if (n.children && walk(n.children)) return true;
        }
        return false;
      };
      walk(state.virtualTree.children);
    }

    // 7. 평면화된 데이터 업데이트
    if (getPanelFlattenedRegistry()?.byString) {
      const string = getPanelFlattenedRegistry().byString.get(stringId);
      if (string) {
        string.name = newName;
        string.seq = newSeq;
      }
    }
  },

  // 🎯 가상 트리에서 String 이름 업데이트
  updateStringNameInVirtualTree(state, stringId, newName, newSeq) {
    const updateNodeRecursively = nodes => {
      for (const node of nodes) {
        if (node.id === stringId && node.type === 'string') {
          node.name = newName;
          node.seq = newSeq;
          return true;
        }
        if (node.children && updateNodeRecursively(node.children)) {
          return true;
        }
      }
      return false;
    };

    updateNodeRecursively(state.virtualTree.children);
  },

  // 🎯 Array 이름 변경 (낙관적 업데이트)
  updateArrayNameOptimistically(state, { arrayId, newName, newSeq }) {
    // 1. 원본 상태 저장 (롤백용)
    const arrayFeature = state.derivedMapData.get(`array_${arrayId}`);
    if (arrayFeature && !state.optimisticUpdates.originalStates.has(`array_${arrayId}`)) {
      state.optimisticUpdates.originalStates.set(`array_${arrayId}`, {
        name: arrayFeature.properties.name,
        seq: arrayFeature.properties.seq,
        type: 'name'
      });
    }

    // 2. 임시 변경사항 저장
    state.optimisticUpdates.pendingChanges.set(`array_${arrayId}`, {
      type: 'name',
      newValue: { name: newName, seq: newSeq },
      timestamp: Date.now()
    });

    // 3. 낙관적 모드 활성화
    state.optimisticUpdates.isOptimisticMode = true;

    // 4. Tree 데이터 즉시 업데이트
    const arrayGroup = getPanelTreeLocalRegistry().find(array => array.id === arrayId);
    if (arrayGroup) {
      arrayGroup.name = newName;
      arrayGroup.seq = newSeq;
    }
    notifyPanelTreeLocalChange();

    // 5. 지도 데이터 즉시 업데이트
    if (arrayFeature) {
      arrayFeature.properties.name = newName;
      arrayFeature.properties.seq = newSeq;
      state.derivedMapData.set(`array_${arrayId}`, arrayFeature);
    }

    // 6. 가상 트리 업데이트
    if (state.virtualTree) {
      const walk = nodes => {
        for (const n of nodes) {
          if (n.id === arrayId && n.type === 'array') {
            n.name = newName;
            n.seq = newSeq;
            return true;
          }
          if (n.children && walk(n.children)) return true;
        }
        return false;
      };
      walk(state.virtualTree.children);
    }

    // 7. 평면화된 데이터 업데이트
    if (getPanelFlattenedRegistry()?.byArray) {
      const array = getPanelFlattenedRegistry().byArray.get(arrayId);
      if (array) {
        array.name = newName;
        array.seq = newSeq;
      }
    }
  },

  // 🎯 가상 트리에서 Array 이름 업데이트
  updateArrayNameInVirtualTree(state, arrayId, newName, newSeq) {
    const updateNodeRecursively = nodes => {
      for (const node of nodes) {
        if (node.id === arrayId && node.type === 'array') {
          node.name = newName;
          node.seq = newSeq;
          return true;
        }
        if (node.children && updateNodeRecursively(node.children)) {
          return true;
        }
      }
      return false;
    };

    updateNodeRecursively(state.virtualTree.children);
  },

  // 낙관적 업데이트 완료 처리
  commitOptimisticUpdates(state) {
    // 임시 변경사항들을 서버 데이터에 반영
    state.optimisticUpdates.pendingChanges.forEach((change, id) => {
      // 서버 데이터 업데이트 로직 (필요한 경우)
      console.log(`🔄 낙관적 업데이트 커밋: ${id}`, change);
    });

    // 임시 상태 초기화
    state.optimisticUpdates.pendingChanges.clear();
    state.optimisticUpdates.originalStates.clear();
    state.optimisticUpdates.isOptimisticMode = false;
  },

  // 롤백 처리
  rollbackOptimisticUpdates(state) {
    console.log('�� 낙관적 업데이트 롤백 시작');
    state.optimisticUpdates.originalStates.forEach((originalState, id) => {
      if (originalState.type === 'position') {
        const moduleFeature = state.derivedMapData.get(id);
        if (moduleFeature) {
          moduleFeature.geometry = originalState.geometry;
          state.derivedMapData.set(id, moduleFeature);
        }
      } else if (originalState.type === 'name') {
        // Tree 데이터 롤백 (logic for module and string names)
        // 지도 데이터 롤백
        const feature = state.derivedMapData.get(id);
        if (feature) {
          feature.properties.name = originalState.name;
          state.derivedMapData.set(id, feature);
        }
      } else if (originalState.type === 'stringMove') {
        const moduleFeature = state.derivedMapData.get(id);
        if (moduleFeature) {
          moduleFeature.properties.solarStringId = originalState.solarStringId;
          state.derivedMapData.set(id, moduleFeature);
        }
      } else if (originalState.type === 'bulkUpdate') {
        const moduleFeature = state.derivedMapData.get(id);
        if (moduleFeature) {
          Vue.set(moduleFeature, 'geometry', originalState.geometry);
          Vue.set(moduleFeature.properties, 'solarStringId', originalState.solarStringId);
          state.derivedMapData.set(id, moduleFeature);
        }
      }
    });

    // createString 타입의 임시 피처들 제거
    state.optimisticUpdates.pendingChanges.forEach((change, id) => {
      if (change.type === 'createString') {
        // 임시 String 피처 제거
        state.derivedMapData.delete(id);

        // 임시 Module 피처들 제거
        if (change.tempModuleIds) {
          change.tempModuleIds.forEach(moduleId => {
            state.derivedMapData.delete(moduleId);
          });
        }
      }
    });

    state.optimisticUpdates.pendingChanges.clear();
    state.optimisticUpdates.originalStates.clear();
    state.optimisticUpdates.isOptimisticMode = false;
    console.log('🔄 낙관적 업데이트 롤백 완료');
  },

  // 드래그 앤 드롭 관련 mutations
  moveModuleToNewStringOptimistically(state, { moduleId, targetStringId }) {
    // 1. 원본 상태 저장 (롤백용)
    const moduleFeature = state.derivedMapData.get(`module_${moduleId}`);
    if (moduleFeature && !state.optimisticUpdates.originalStates.has(`module_${moduleId}`)) {
      state.optimisticUpdates.originalStates.set(`module_${moduleId}`, {
        solarStringId: moduleFeature.properties.solarStringId,
        type: 'stringMove'
      });
    }

    // 2. 임시 변경사항 저장
    state.optimisticUpdates.pendingChanges.set(`module_${moduleId}`, {
      type: 'stringMove',
      newValue: targetStringId,
      timestamp: Date.now()
    });

    // 3. 낙관적 모드 활성화
    state.optimisticUpdates.isOptimisticMode = true;

    // 4. 지도 데이터 즉시 업데이트
    if (moduleFeature) {
      moduleFeature.properties.solarStringId = targetStringId;
      state.derivedMapData.set(`module_${moduleId}`, moduleFeature);
    }
  },

  // 대량 작업 관련 mutations
  bulkUpdateModulesOptimistically(state, { moduleUpdates }) {
    moduleUpdates.forEach(update => {
      const moduleFeature = state.derivedMapData.get(`module_${update.moduleId}`);
      if (moduleFeature) {
        // 1. 원본 상태 저장 (롤백용)
        if (!state.optimisticUpdates.originalStates.has(`module_${update.moduleId}`)) {
          state.optimisticUpdates.originalStates.set(`module_${update.moduleId}`, {
            geometry: moduleFeature.geometry,
            solarStringId: moduleFeature.properties.solarStringId,
            type: 'bulkUpdate'
          });
        }

        // 2. 임시 변경사항 저장
        state.optimisticUpdates.pendingChanges.set(`module_${update.moduleId}`, {
          type: 'bulkUpdate',
          newPosition: update.newPosition,
          newStringId: update.targetStringId,
          timestamp: Date.now()
        });

        // 3. 지도 데이터 즉시 업데이트
        Vue.set(moduleFeature, 'geometry', update.newPosition);
        Vue.set(moduleFeature.properties, 'solarStringId', update.targetStringId);
        state.derivedMapData.set(`module_${update.moduleId}`, moduleFeature);
      }
    });

    // 4. 낙관적 모드 활성화
    state.optimisticUpdates.isOptimisticMode = true;
  },

  /**
   * Tree 데이터 관련 mutations
   */
  SET_TREE_DATA(state, treeData) {
    void state;
    setPanelTreeLocalRegistry(treeData);
    // 하위 호환 — 기존 [panel-perf] moduleTree:SET_TREE_DATA 라벨 유지
    panelPerfCountSummary('moduleTree:SET_TREE_DATA:counts', {
      arrays: getPanelTreeLocalRegistry()?.length ?? 0,
      ...(() => {
        let strings = 0;
        let modulesHydrated = 0;
        let modulesLazy = 0;
        (treeData || []).forEach(arrayNode => {
          const list = arrayNode?.children || [];
          strings += list.length;
          list.forEach(stringNode => {
            const hydrated = stringNode?.children?.length ?? 0;
            if (hydrated > 0) modulesHydrated += hydrated;
            else modulesLazy += stringNode?.moduleChildCount ?? 0;
          });
        });
        return { strings, modulesHydrated, modulesLazy };
      })()
    });
  },

  ADD_ARRAY_TO_TREE(state, newArray) {
    void state;
    addArrayToPanelTree(newArray);
  },

  ADD_STRING_TO_TREE(state, payload) {
    void state;
    addStringToPanelTree(payload?.arrayNodeId, payload?.stringNode);
  },

  REMOVE_ARRAY_FROM_TREE(state, index) {
    void state;
    removeArrayFromPanelTreeByIndex(index);
  },

  UPDATE_TREE_DATA(state, treeData) {
    void state;
    setPanelTreeLocalRegistry(treeData);
  },

  REMOVE_ARRAY_TREE_NODE(state, arrayNodeId) {
    void state;
    removePanelTreeNodeById(arrayNodeId);
  },

  REMOVE_STRING_TREE_NODE(state, stringNodeId) {
    void state;
    removePanelTreeNodeById(stringNodeId);
  },

  REMOVE_MODULE_TREE_NODE(state, moduleNodeId) {
    void state;
    removePanelTreeNodeById(moduleNodeId);
  },

  RENAME_TREE_NODE(state, payload) {
    void state;
    renamePanelTreeNode(payload?.nodeId, payload?.newName);
  },

  /**
   * 상태 관리 mutations
   */
  SET_CREATING_STATE(state, isCreating) {
    state.isCreatingState = isCreating;
  },

  SET_UPDATING_STATE(state, isUpdating) {
    state.isUpdatingState = isUpdating;
  },

  SET_DELETING_STATE(state, isDeleting) {
    state.isDeletingState = isDeleting;
  },

  SET_ERROR_STATE(state, hasError) {
    state.errorState = hasError;
  },

  SET_ERROR_MESSAGE(state, message) {
    state.errorMessage = message;
  },

  SET_SUCCESS_STATE(state, hasSuccess) {
    state.successState = hasSuccess;
  },

  SET_SUCCESS_MESSAGE(state, message) {
    state.successMessage = message;
  }
};

const actions = {
  // 🎯 서버 데이터 관련 actions (단일 진실 소스)
  async fetchSiteDesignData({ commit, dispatch }, siteId) {
    try {
      commit('setLoadingState', true);
      commit('clearErrorState');

      const siteDesignData = await siteDesignApi.getSiteDesign(siteId);
      commit('setServerSiteDesignData', siteDesignData);

      // 서버 데이터가 변경되면 파생 데이터 자동 생성
      await dispatch('generateDerivedDataFromServerData');

      return siteDesignData;
    } catch (error) {
      commit('setErrorState', {
        error,
        message: i18n.t('moduleTree.loadSiteDesignFailed', { message: error.message })
      });
      throw error;
    } finally {
      commit('setLoadingState', false);
    }
  },

  setSelectedSite({ commit }, siteData) {
    commit('setSelectedSiteData', siteData);
  },

  // 🎯 평면화된 데이터 생성 actions
  async generateFlattenedData({ commit, state }) {
    if (!state.serverSiteDesignData) {
      commit('setFlattenedData', null);
      return;
    }

    try {
      const flattenedData = flattenSiteDesignData(state.serverSiteDesignData);
      commit('setFlattenedData', flattenedData);
      console.log('🔄 평면화된 데이터 생성 완료');
    } catch (error) {
      commit('setErrorState', {
        error,
        message: i18n.t('moduleTree.flattenFailed', { message: error.message })
      });
      throw error;
    }
  },

  // 🎯 가상 트리 생성 actions
  async generateVirtualTree({ commit, state }) {
    if (!getPanelFlattenedRegistry()) {
      commit('setVirtualTree', null);
      return;
    }

    try {
      const virtualTree = this.generateVirtualTreeFromFlattenedData({ state });
      commit('setVirtualTree', virtualTree);
      console.log('🔄 가상 트리 생성 완료');
    } catch (error) {
      commit('setErrorState', {
        error,
        message: i18n.t('moduleTree.virtualTreeFailed', { message: error.message })
      });
      throw error;
    }
  },

  // 🎯 평면화된 데이터에서 가상 트리 생성
  generateVirtualTreeFromFlattenedData({ state }) {
    const virtualTree = {
      id: 'root',
      name: 'Root',
      type: 'root',
      children: []
    };

    if (!getPanelFlattenedRegistry()) return virtualTree;

    const flat = getPanelFlattenedRegistry();
    const arrayGroups = flat.arrayGroups;
    const strings = flat.strings;
    const modules = flat.modules;

    // Array Groups
    arrayGroups.forEach(arrayGroup => {
      const arrayNode = {
        id: arrayGroup.id,
        name: arrayGroup.name,
        type: 'array',
        children: []
      };
      virtualTree.children.push(arrayNode);
    });

    // Strings
    strings.forEach(string => {
      const stringNode = {
        id: string.id,
        name: string.name,
        type: 'string',
        children: []
      };
      // Find the parent array group
      const parentArray = virtualTree.children.find(group => group.id === string.arrayGroupId);
      if (parentArray) {
        parentArray.children.push(stringNode);
      }
    });

    // Modules
    modules.forEach(module => {
      const moduleNode = {
        id: module.id,
        name: module.name,
        type: 'module',
        children: []
      };
      // Find the parent string
      const parentString = virtualTree.children.find(string => string.id === module.stringId);
      if (parentString) {
        parentString.children.push(moduleNode);
      }
    });

    return virtualTree;
  },

  // 🎯 파생 데이터 생성 actions
  async generateDerivedDataFromServerData({ commit, state }) {
    if (!state.selectedSiteData || !state.serverSiteDesignData) {
      commit('setDerivedTreeData', []);
      commit('setDerivedMapData', new Map());
      return;
    }

    try {
      // Tree 데이터 생성
      const treeData = await this.dispatch('panel/generateTreeDataFromServerData');
      commit('setDerivedTreeData', treeData);

      // 지도 데이터 생성
      const mapData = await this.dispatch('panel/generateMapDataFromServerData');
      commit('setDerivedMapData', mapData);

      console.log('🔄 파생 데이터 생성 완료');
    } catch (error) {
      commit('setErrorState', {
        error,
        message: i18n.t('moduleTree.derivedDataFailed', { message: error.message })
      });
      throw error;
    }
  },

  // Tree 데이터 생성 (서버 데이터 기반)
  async generateTreeDataFromServerData({ state }) {
    const treeData = [];
    let arrayGroupId = 1;

    // 서버 데이터에서 SolarString들을 추출
    const solarStrings = [];
    if (state.serverSiteDesignData?.solarSections) {
      state.serverSiteDesignData.solarSections.forEach(section => {
        if (section?.solarArrays) {
          section.solarArrays.forEach(solarArray => {
            if (solarArray?.solarStrings) {
              solarArray.solarStrings.forEach(solarString => {
                solarStrings.push(solarString);
              });
            }
          });
        }
      });
    }

    // SolarString들을 Array 그룹별로 정리
    const groupedStrings = solarStrings.reduce((groups, solarString) => {
      const arrayId = solarString.solarArrayId || 'default';
      if (!groups[arrayId]) {
        groups[arrayId] = [];
      }
      groups[arrayId].push(solarString);
      return groups;
    }, {});

    // 각 Array Group 생성
    Object.keys(groupedStrings).forEach(arrayId => {
      const strings = groupedStrings[arrayId];
      const arrayPk = /^\d+$/.test(String(arrayId)) ? Number(arrayId) : null;
      const arrayGroup = {
        name: `Array Group ${arrayGroupId}`,
        id: arrayPk != null ? `array_${arrayPk}` : `array_${arrayGroupId}`,
        solarArraySeq: arrayPk ?? arrayId,
        solarArrayId: arrayId,
        arrayId: arrayPk ?? arrayId,
        type: 'array',
        children: []
      };

      // 각 String을 자식으로 추가
      strings.forEach(solarString => {
        const stringDomain = stringFromApi(solarString);
        const stringNode = {
          ...stringDomainToTreeFields(stringDomain),
          name: `String ${stringDomain.displayNo ?? ''}`,
          children: []
        };

        // 각 Module을 자식으로 추가
        if (solarString.solarModules && solarString.solarModules.length > 0) {
          solarString.solarModules.forEach(module => {
            const moduleDomain = moduleFromApi(module);
            stringNode.children.push({
              ...moduleDomainToTreeFields(moduleDomain),
              children: []
            });
          });
        }

        arrayGroup.children.push(stringNode);
      });

      treeData.push(arrayGroup);
      arrayGroupId++;
    });

    console.log('🌳 Tree 데이터 생성 완료:', treeData);
    return treeData;
  },

  // 지도 데이터 생성 (서버 데이터 기반)
  async generateMapDataFromServerData({ state }) {
    const mapData = new Map();

    // 서버 데이터에서 지도 피처 생성
    if (state.serverSiteDesignData?.solarSections) {
      state.serverSiteDesignData.solarSections.forEach(section => {
        if (section?.solarArrays) {
          section.solarArrays.forEach(solarArray => {
            if (solarArray?.solarStrings) {
              solarArray.solarStrings.forEach(solarString => {
                // String 피처 생성
                const stringFeature = this.createStringFeature(solarString);
                mapData.set(`string_${solarString.stringId}`, stringFeature);

                // Module 피처들 생성
                if (solarString.solarModules) {
                  solarString.solarModules.forEach(module => {
                    const moduleFeature = this.createModuleFeature(module);
                    mapData.set(`module_${module.moduleId}`, moduleFeature);
                  });
                }
              });
            }
          });
        }
      });
    }

    console.log('🗺️ 지도 데이터 생성 완료:', mapData.size, '개 피처');
    return mapData;
  },

  // 피처 생성 헬퍼 메서드들
  createStringFeature(solarString) {
    // String 피처 생성 로직
    return {
      id: `string_${solarString.stringId}`,
      type: 'string',
      geometry: solarString.shapeGeog,
      properties: {
        name: `String ${solarString.stringId}`,
        featureType: 'String',
        solarStringId: solarString.stringId,
        colNum: solarString.colNum,
        rowNum: solarString.rowNum,
        isTemporary: true
      }
    };
  },

  createModuleFeature(module) {
    // Module 피처 생성 로직
    return {
      id: `module_${module.moduleId}`,
      type: 'module',
      geometry: module.shapeGeog,
      properties: {
        name: `Module ${module.moduleId}`,
        featureType: 'Module',
        solarModuleId: module.moduleId,
        solarStringId: module.solarStringId,
        isTemporary: true
      }
    };
  },

  // Array 관리 actions (서버 데이터 변경 후 파생 데이터 자동 업데이트)
  async createArrayGroup({ commit, state, dispatch }, arrayData) {
    try {
      commit('setCreatingState', true);
      commit('clearErrorState');

      // Array 관리 서비스 초기화
      if (state.selectedSiteData) {
        arrayManagementService.initialize(state.selectedSiteData.id);
      }

      // 데이터 검증
      arrayManagementService.validateArrayData(arrayData);

      // 데이터 정규화
      const normalizedData = arrayManagementService.normalizeArrayData(arrayData);

      // API 호출
      const response = await arrayManagementService.createArray(normalizedData);

      // 서버 데이터 새로고침 (파생 데이터 자동 업데이트)

      commit('setSuccessState', {
        success: true,
        message: i18n.t('moduleTree.arrayAdded', { name: normalizedData.name })
      });

      return response;
    } catch (error) {
      commit('setErrorState', {
        error,
        message: i18n.t('moduleTree.arrayAddFailed', { message: error.message })
      });
      throw error;
    } finally {
      commit('setCreatingState', false);
    }
  },

  async updateArrayGroupName({ commit, state, dispatch }, { arrayId, newName }) {
    try {
      commit('setUpdatingState', true);
      commit('clearErrorState');

      // Array 관리 서비스 초기화
      if (state.selectedSiteData) {
        arrayManagementService.initialize(state.selectedSiteData.id);
      }

      // API 호출
      await arrayManagementService.updateArrayName(arrayId, newName);

      // 서버 데이터 새로고침 (파생 데이터 자동 업데이트)

      commit('setSuccessState', {
        success: true,
        message: i18n.t('moduleTree.arrayRenamed')
      });
    } catch (error) {
      commit('setErrorState', {
        error,
        message: i18n.t('moduleTree.arrayRenameFailed', { message: error.message })
      });
      throw error;
    } finally {
      commit('setUpdatingState', false);
    }
  },

  async deleteArrayGroup({ commit, state, dispatch }, arrayId) {
    try {
      commit('setDeletingState', true);
      commit('clearErrorState');

      // Array 관리 서비스 초기화
      if (state.selectedSiteData) {
        arrayManagementService.initialize(state.selectedSiteData.id);
      }

      // API 호출
      await arrayManagementService.deleteArray(arrayId);

      // 서버 데이터 새로고침 (파생 데이터 자동 업데이트)

      commit('setSuccessState', {
        success: true,
        message: i18n.t('moduleTree.arrayDeleted')
      });
    } catch (error) {
      commit('setErrorState', {
        error,
        message: i18n.t('moduleTree.arrayDeleteFailed', { message: error.message })
      });
      throw error;
    } finally {
      commit('setDeletingState', false);
    }
  },

  // 🎯 선택 상태 관리 actions
  selectNode({ commit }, nodeId) {
    commit('addSelectedNode', nodeId);
  },

  deselectNode({ commit }, nodeId) {
    commit('removeSelectedNode', nodeId);
  },

  selectMultipleNodes({ commit }, nodeIds) {
    commit('setSelectedNodeCollection', nodeIds);
  },

  clearAllSelectedNodes({ commit }) {
    commit('clearSelectedNodes');
  },

  // 🎯 편집 상태 관리 actions
  startEditingNode({ commit }, nodeId) {
    commit('setEditingNodeId', nodeId);
  },

  stopEditingNode({ commit }) {
    commit('setEditingNodeId', null);
  },

  // 🎯 드래그 상태 관리 actions
  startDraggingNode({ commit }, nodeId) {
    commit('setDraggingState', { isDragging: true, draggedNodeId: nodeId });
  },

  stopDraggingNode({ commit }) {
    commit('setDraggingState', { isDragging: false, draggedNodeId: null });
  },

  // 🎯 동기화 actions
  async synchronizeTreeWithMap({ commit }) {
    try {
      commit('setSynchronizationState', { isSynchronizing: true });
      commit('clearSynchronizationErrors');

      // Tree와 지도 간 동기화 로직
      // TODO: 실제 동기화 로직 구현

      commit('setSynchronizationState', {
        isSynchronizing: false,
        lastSynchronizationTime: new Date()
      });
    } catch (error) {
      commit('addSynchronizationError', error);
      commit('setSynchronizationState', { isSynchronizing: false });
      throw error;
    }
  },

  // 🎯 에러/성공 상태 관리 actions
  clearError({ commit }) {
    commit('clearErrorState');
  },

  clearSuccess({ commit }) {
    commit('clearSuccessState');
  },

  // 🎯 사용자 인터랙션 관련 actions (낙관적 업데이트)
  async updateModulePosition({ commit, state, dispatch }, { moduleId, newPosition }) {
    try {
      // 1. 즉시 UI 업데이트 (낙관적 업데이트)
      commit('updateModulePositionOptimistically', { moduleId, newPosition });

      // 2. 서버에 변경사항 전송
      await updatePosition(moduleId, newPosition);

      // 3. 성공 시 낙관적 업데이트 커밋
      commit('commitOptimisticUpdates');

      // 4. 서버 데이터 새로고침 (최종 동기화)

      commit('setSuccessState', {
        success: true,
        message: i18n.t('moduleTree.modulePositionUpdated')
      });
    } catch (error) {
      // 5. 실패 시 롤백
      commit('rollbackOptimisticUpdates');
      commit('setErrorState', { error, message: i18n.t('moduleTree.modulePositionFailed') });
      throw error;
    }
  },

  async updateModuleName({ commit, state, dispatch }, { moduleId, newName }) {
    try {
      // 1. 즉시 UI 업데이트 (낙관적 업데이트)
      commit('updateModuleNameOptimistically', { moduleId, newName });

      // 2. 서버에 변경사항 전송
      await updateName(moduleId, newName);

      // 3. 성공 시 낙관적 업데이트 커밋
      commit('commitOptimisticUpdates');

      // 4. 서버 데이터 새로고침 (최종 동기화)

      commit('setSuccessState', { success: true, message: i18n.t('moduleTree.moduleNameUpdated') });
    } catch (error) {
      // 5. 실패 시 롤백
      commit('rollbackOptimisticUpdates');
      commit('setErrorState', { error, message: i18n.t('moduleTree.moduleNameFailed') });
      throw error;
    }
  },

  async updateStringName({ commit, state, dispatch }, { siteId, stringId, newName }) {
    try {
      // 1. 즉시 UI 업데이트 (낙관적 업데이트)
      commit('updateStringNameOptimistically', { stringId, newName });

      // 2. 서버에 변경사항 전송
      await updateStringNameApi(stringId, newName);

      // 3. 성공 시 낙관적 업데이트 커밋
      commit('commitOptimisticUpdates');

      // 4. 서버 데이터 새로고침 (최종 동기화)

      commit('setSuccessState', { success: true, message: i18n.t('moduleTree.stringNameUpdated') });
    } catch (error) {
      // 5. 실패 시 롤백
      commit('rollbackOptimisticUpdates');
      commit('setErrorState', { error, message: i18n.t('moduleTree.stringNameFailed') });
      throw error;
    }
  },

  // 드래그 앤 드롭 관련 actions
  async moveModuleToNewString({ commit, state, dispatch }, { moduleId, targetStringId }) {
    try {
      // 1. 즉시 UI 업데이트 (낙관적 업데이트)
      commit('moveModuleToNewStringOptimistically', { moduleId, targetStringId });

      // 2. 서버에 변경사항 전송
      await moveToNewString(moduleId, targetStringId);

      // 3. 성공 시 낙관적 업데이트 커밋
      commit('commitOptimisticUpdates');

      // 4. 서버 데이터 새로고침 (최종 동기화)

      commit('setSuccessState', { success: true, message: i18n.t('moduleTree.moduleMoved') });
    } catch (error) {
      // 5. 실패 시 롤백
      commit('rollbackOptimisticUpdates');
      commit('setErrorState', { error, message: i18n.t('moduleTree.moduleMoveFailed') });
      throw error;
    }
  },

  // 대량 작업 관련 actions
  async bulkUpdateModules({ commit, state, dispatch }, { moduleUpdates }) {
    try {
      commit('setBulkProcessingState', true);

      // 1. 즉시 UI 업데이트 (낙관적 업데이트)
      commit('bulkUpdateModulesOptimistically', { moduleUpdates });

      // 2. 서버에 변경사항 전송
      await bulkUpdate(moduleUpdates);

      // 3. 성공 시 낙관적 업데이트 커밋
      commit('commitOptimisticUpdates');

      // 4. 서버 데이터 새로고침 (최종 동기화)

      commit('setSuccessState', { success: true, message: i18n.t('moduleTree.bulkUpdateDone') });
    } catch (error) {
      // 5. 실패 시 롤백
      commit('rollbackOptimisticUpdates');
      commit('setErrorState', { error, message: i18n.t('moduleTree.bulkUpdateFailed') });
      throw error;
    } finally {
      commit('setBulkProcessingState', false);
    }
  },

  /**
   * 새로운 SolarString과 Module들을 생성
   */
  async createSolarStringWithModules({ commit, state, dispatch }, { siteId, solarStringData }) {
    try {
      console.log('🚀 Store: 새로운 SolarString과 Module 생성 시작');

      // 낙관적 업데이트: 임시 피처들을 derivedMapData에 추가
      const tempStringId = `temp_string_${Date.now()}`;
      const tempModuleIds = [];

      // 임시 String 피처 생성
      const tempStringFeature = {
        id: tempStringId,
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: [solarStringData.shapeCoordinates]
        },
        properties: {
          name: `String ${solarStringData.stringId}`,
          featureType: 'String',
          solarStringId: solarStringData.stringId,
          colNum: solarStringData.colNum,
          rowNum: solarStringData.rowNum,
          isTemporary: true
        }
      };

      // 임시 Module 피처들 생성
      solarStringData.solarModules.forEach((module, index) => {
        const tempModuleId = `temp_module_${Date.now()}_${index}`;
        tempModuleIds.push(tempModuleId);

        const tempModuleFeature = {
          id: tempModuleId,
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [module.shapeCoordinates]
          },
          properties: {
            name: `Module ${module.moduleId}`,
            featureType: 'Module',
            solarModuleId: module.moduleId,
            solarStringId: solarStringData.stringId,
            isTemporary: true
          }
        };

        // derivedMapData에 임시 피처 추가
        state.derivedMapData.set(tempModuleId, tempModuleFeature);
      });

      // derivedMapData에 임시 String 피처 추가
      state.derivedMapData.set(tempStringId, tempStringFeature);

      // 낙관적 업데이트 상태 설정
      state.optimisticUpdates.isOptimisticMode = true;
      state.optimisticUpdates.pendingChanges.set(tempStringId, {
        type: 'createString',
        data: solarStringData,
        tempModuleIds,
        timestamp: Date.now()
      });

      // API 호출
      const response = await siteDesignApi.addSolarStringAndItsModules(siteId, solarStringData);
      console.log('🔄 서버 응답:', response);

      // 성공 시 낙관적 업데이트 커밋 및 서버 데이터 재동기화
      commit('commitOptimisticUpdates');

      commit('setSuccessState', {
        success: true,
        message: i18n.t('moduleTree.stringModuleCreated')
      });

      return response;
    } catch (error) {
      console.error('❌ Store: SolarString 생성 실패:', error);

      // 실패 시 낙관적 업데이트 롤백
      commit('rollbackOptimisticUpdates');

      commit('setErrorState', { error, message: i18n.t('moduleTree.stringModuleCreateFailed') });
      throw error;
    }
  },

  /**
   * PanelPlanningPage.vue의 Tree 관련 메서드들을 통합
   * 기존 로직을 최대한 유지하면서 store로 이동
   */

  /**
   * Array 추가 확인 및 실행 (기존 confirmAddArray 대체)
   */
  async confirmAddArray({ commit }, { siteId, arrayData, sectionId }) {
    commit('SET_CREATING_STATE', true);
    commit('SET_ERROR_STATE', false);
    commit('SET_SUCCESS_STATE', false);

    try {
      if (!siteId) {
        throw new Error('Site not selected');
      }

      // Array 관리 서비스 초기화
      const { arrayManagementService } = await import('../../../services/ArrayManagementService');
      arrayManagementService.initialize(siteId);

      const nextArrayNumber = resolveNextArrayDisplayId({
        flattened: getPanelFlattenedRegistry(),
        treeDisplayNumbers: collectPanelTreeArrayDisplayNumbers()
      });
      const autoGeneratedName = `Array ${nextArrayNumber}`;

      // Array 데이터 준비 — arrayId(표시용 번호)도 함께 저장해 재조회 시 번호가 유지되게 한다
      const normalizedData = {
        name: autoGeneratedName,
        arrayId: String(nextArrayNumber),
        ...arrayData
      };

      // 데이터 검증
      arrayManagementService.validateArrayData(normalizedData);

      // API 호출 → 성공/실패만 반환 (트리 갱신은 컴포넌트에서 처리)
      const response = await arrayManagementService.createArray(normalizedData, sectionId);

      commit('SET_SUCCESS_STATE', true);
      commit('SET_SUCCESS_MESSAGE', i18n.t('moduleTree.arrayAdded', { name: normalizedData.name }));

      return {
        success: true,
        data: response,
        name: normalizedData.name,
        arrayDisplayId: normalizedData.arrayId
      };
    } catch (error) {
      console.error('❌ Array 추가 실패:', error);
      commit('SET_ERROR_STATE', true);
      commit('SET_ERROR_MESSAGE', error.message || i18n.t('moduleTree.arrayAddFailed', { message: i18n.t('common.unknownError') }));
      throw error;
    } finally {
      commit('SET_CREATING_STATE', false);
    }
  },

  /**
   * TreeNode에서 발생하는 이동 이벤트 처리 (기존 handleNodeMoved 대체)
   */
  async handleNodeMoved(
    { commit, dispatch, state },
    {
      sourceItem,
      targetItem,
      sourceGroupName,
      targetGroupName,
      numberingAssignment = null
    }
  ) {
    commit('SET_UPDATING_STATE', true);
    commit('SET_ERROR_STATE', false);

    try {
      if (sourceGroupName === 'string' && targetGroupName === 'array') {
        // String을 다른 Array로 이동
        await dispatch('moveStringToArray', {
          sourceString: sourceItem,
          targetArray: targetItem,
          numberingAssignment
        });

        commit('SET_SUCCESS_STATE', true);
        commit(
          'SET_SUCCESS_MESSAGE',
          i18n.t('moduleTree.stringMovedTo', {
            name: sourceItem.name,
            target: targetItem.name
          })
        );
      } else if (sourceGroupName === 'module' && targetGroupName === 'string') {
        await dispatch('moveModuleToString', {
          sourceModule: sourceItem,
          targetString: targetItem,
          numberingAssignment
        });

        commit('SET_SUCCESS_STATE', true);
        commit('SET_SUCCESS_MESSAGE', i18n.t('moduleTree.moduleMoved'));
      }
    } catch (error) {
      console.error('❌ Node 이동 실패:', error);
      commit('SET_ERROR_STATE', true);
      commit('SET_ERROR_MESSAGE', error.message || i18n.t('moduleTree.nodeMoveFailed'));
      throw error;
    } finally {
      commit('SET_UPDATING_STATE', false);
    }
  },

  /**
   * String을 다른 Array로 이동 (기존 moveStringToArray 로직)
   */
  async moveStringToArray(
    { commit, state },
    { sourceString, targetArray, numberingAssignment = null }
  ) {
    console.log(`🎯 String 이동: ${sourceString.name} → ${targetArray.name}`);

    const flatCtx = { flattenedSiteDesign: getPanelFlattenedRegistry() };
    const sourceStringId = resolveSolarStringSeqFromTreeItem(sourceString, flatCtx);
    const targetArrayId = resolveSolarArraySeqFromTreeItem(targetArray, flatCtx);

    if (
      sourceStringId == null ||
      sourceStringId === '' ||
      targetArrayId == null ||
      targetArrayId === ''
    ) {
      console.error('String 또는 Array ID 누락:', { sourceStringId, targetArrayId });
      throw new Error(i18n.t('panelPlanning.arrayIdNotFound'));
    }

    // API 호출
    await moveStringToArrayAPI(sourceStringId, targetArrayId);
    if (numberingAssignment?.changed) {
      const nextName = numberingAssignment.name || sourceString.name;
      await updateStringNameApi(sourceStringId, nextName, {
        displayId: numberingAssignment.displayNo
      });
    }

    // in-place mutation — 배열 교체 대신 splice/push로 트리 확장 상태 보존
    const moved = moveStringInPanelTreeInPlace(sourceString, targetArray);
    if (!moved) {
      console.warn('[moveStringToArray] sourceString not found in any array children');
    }

    console.log(`✅ String 이동 완료: ${sourceString.name}`);
  },

  /**
   * Module → 다른 String 이동
   * API: PUT /api/solarModules/{id}/solarString (text/uri-list)
   */
  async moveModuleToString(
    { commit, state },
    { sourceModule, targetString, numberingAssignment = null }
  ) {
    console.log(`🎯 Module 이동: ${sourceModule.name} → ${targetString.name}`);

    const flatCtx = { flattenedSiteDesign: getPanelFlattenedRegistry() };
    const moduleId = resolveSolarModuleDeleteKeyFromTreeItem(sourceModule, flatCtx);
    const targetStringId = resolveSolarStringSeqFromTreeItem(targetString, flatCtx);

    if (moduleId == null || moduleId === '' || targetStringId == null || targetStringId === '') {
      console.error('Module 또는 String ID 누락:', { moduleId, targetStringId });
      throw new Error(i18n.t('moduleTree.moduleMoveFailed'));
    }

    await moveToNewString(moduleId, targetStringId);
    if (numberingAssignment?.changed) {
      const nextName = numberingAssignment.name || sourceModule.name;
      await updateName(moduleId, nextName, {
        displayId: numberingAssignment.displayNo
      });
    }

    // 트리 UI는 PanelPlanningPage.moveModuleInFlattenedSiteDesign(flattened 기준)에서 동기화
    console.log(`✅ Module 이동 완료: ${sourceModule.name}`);
  },

  /**
   * Array 이름 변경 처리 (기존 handleNameChanged 대체)
   */
  async handleNameChanged(
    { commit, state },
    { itemId, nodeId, oldName, newName, siteId, itemType }
  ) {
    const canonicalName = normalizeEntityRenameName(itemType, newName);
    console.log('🏪 Store handleNameChanged 시작:', {
      itemId,
      nodeId,
      oldName,
      newName,
      canonicalName,
      siteId,
      itemType
    });

    // siteId 검증
    if (!siteId) {
      throw new Error(i18n.t('common.siteIdRequiredSelectFirst'));
    }

    commit('SET_UPDATING_STATE', true);
    commit('SET_ERROR_STATE', false);

    try {
      let successMessage = '';

      // 아이템 타입에 따른 API 호출
      if (itemType === 'array') {
        // Array 이름 변경
        if (siteId) {
          arrayManagementService.initialize(siteId);
          await arrayManagementService.updateArrayName(itemId, canonicalName);
        }
        successMessage = i18n.t('moduleTree.entityRenamed', {
          type: 'Array',
          oldName,
          newName: canonicalName
        });
      } else if (itemType === 'string') {
        // String 이름 변경 — PATCH /api/solarStrings/{solarStringSeq}
        if (siteId) {
          if (itemId == null || itemId === '') {
            throw new Error('String entity seq(solarStringSeq) is required for rename');
          }
          await updateStringNameApi(itemId, canonicalName);
        }
        successMessage = i18n.t('moduleTree.entityRenamed', {
          type: 'String',
          oldName,
          newName: canonicalName
        });
      } else if (itemType === 'module') {
        // Module 이름 변경
        if (siteId) {
          await updateName(itemId, canonicalName);
        }
        successMessage = i18n.t('moduleTree.entityRenamed', {
          type: 'Module',
          oldName,
          newName: canonicalName
        });
      } else {
        throw new Error(i18n.t('moduleTree.unsupportedItemType', { type: itemType }));
      }

      // 로컬 트리·flattened·맵 반영은 호출측(onTreeNameChanged / applyLocalRenameAfterContextMenu)에서 in-place 처리
      commit('SET_SUCCESS_STATE', true);
      commit('SET_SUCCESS_MESSAGE', successMessage);
    } catch (error) {
      console.error('❌ Array 이름 변경 실패:', error);
      commit('SET_ERROR_STATE', true);
      commit('SET_ERROR_MESSAGE', error.message || i18n.t('moduleTree.arrayRenameFailed', { message: i18n.t('common.unknownError') }));
      throw error;
    } finally {
      commit('SET_UPDATING_STATE', false);
    }
  },

  /**
   * Array 삭제 (기존 deleteArray 대체)
   */
  async deleteArray({ commit, state }, { arrayId, siteId }) {
    commit('SET_DELETING_STATE', true);
    commit('SET_ERROR_STATE', false);

    try {
      // Array 관리 서비스 초기화
      if (siteId) {
        arrayManagementService.initialize(siteId);
      }

      // API 호출
      if (siteId) {
        await arrayManagementService.deleteArray(arrayId);
      }

      // 로컬 데이터에서 제거
      const index = findPanelTreeArrayIndexById(arrayId);
      if (index > -1) {
        commit('REMOVE_ARRAY_FROM_TREE', index);
      }

      commit('SET_SUCCESS_STATE', true);
      commit('SET_SUCCESS_MESSAGE', i18n.t('moduleTree.arrayDeleted'));
    } catch (error) {
      console.error('❌ Array 삭제 실패:', error);
      commit('SET_ERROR_STATE', true);
      commit('SET_ERROR_MESSAGE', error.message || i18n.t('moduleTree.arrayDeleteFailed', { message: i18n.t('common.unknownError') }));
      throw error;
    } finally {
      commit('SET_DELETING_STATE', false);
    }
  },

  /**
   * 맵에서 String 피처들을 이동 (기존 moveStringFeaturesOnMap 로직)
   */
  async moveStringFeaturesOnMap({ commit, state }, { stringItem, targetArray }) {
    try {
      // 지도 데이터에서 해당 String의 모든 피처 찾기
      const stringSeq = readStringSeqFromTreeItem(stringItem);
      const allFeatures = Array.from(state.derivedMapData.values());

      const stringFeatures = allFeatures.filter(
        feature => readStringSeq(feature) === String(stringSeq)
      );

      // Array 정보 업데이트
      stringFeatures.forEach(feature => {
        feature.set('solarArrayId', targetArray.id);
        feature.set('solarArrayName', targetArray.name);
        console.log(`🗺️ 맵 피처 업데이트: ${feature.get('name')} → ${targetArray.name}`);
      });

      // 지도 데이터 업데이트
      stringFeatures.forEach(feature => {
        const featureId = feature.getId();
        commit('updateFeatureInMap', { featureId, updatedFeature: feature });
      });

      console.log('✅ 맵 피처 이동 완료');
    } catch (error) {
      console.error('❌ 맵 피처 이동 실패:', error);
      throw error;
    }
  },

  /**
   * 실제 사이트 데이터를 Tree 구조로 변환 (기존 generateTreeDataFromSite 로직)
   */
  generateTreeDataFromSite({ commit }, { selectedSite, solarStrings }) {
    if (!selectedSite || !solarStrings || solarStrings.length === 0) {
      console.log('🌳 Tree 데이터 생성: 선택된 사이트 또는 데이터가 없음');
      commit('SET_TREE_DATA', []);
      return;
    }

    console.log('🌳 Tree 데이터 생성 시작:', solarStrings);

    const treeData = [];
    let arrayGroupId = 1;

    // SolarStrings를 그룹별로 정리
    const groupedStrings = solarStrings.reduce((groups, solarString) => {
      const arrayId = solarString.solarArrayId || 'default';
      if (!groups[arrayId]) {
        groups[arrayId] = [];
      }
      groups[arrayId].push(solarString);
      return groups;
    }, {});

    // 각 Array Group 생성
    Object.keys(groupedStrings).forEach(arrayId => {
      const strings = groupedStrings[arrayId];
      const arrayPk = /^\d+$/.test(String(arrayId)) ? Number(arrayId) : null;
      const arrayGroup = {
        name: `Array Group ${arrayGroupId}`,
        id: arrayPk != null ? `array_${arrayPk}` : `array_${arrayGroupId}`,
        solarArraySeq: arrayPk ?? arrayId,
        solarArrayId: arrayId,
        arrayId: arrayPk ?? arrayId,
        type: 'array',
        children: []
      };

      // 각 String을 자식으로 추가
      strings.forEach((solarString, stringIndex) => {
        const stringDomain = stringFromApi(solarString);
        const stringNode = {
          ...stringDomainToTreeFields(stringDomain),
          name: `String ${stringDomain.displayNo ?? stringIndex + 1}`,
          children: []
        };

        // 각 Module을 자식으로 추가
        if (solarString.solarModules && solarString.solarModules.length > 0) {
          solarString.solarModules.forEach(module => {
            // moduleId가 유효한 값인지 확인
            if (module.id != null && module.id !== '') {
              const moduleDomain = moduleFromApi(module);
              stringNode.children.push({
                ...moduleDomainToTreeFields(moduleDomain),
                children: []
              });
            }
          });
        }

        arrayGroup.children.push(stringNode);
      });

      treeData.push(arrayGroup);
      arrayGroupId++;
    });

    commit('SET_TREE_DATA', treeData);
    console.log('🌳 Tree 데이터 생성 완료:', treeData);
  },

  /**
   * 최대 모듈 ID와 스트링 ID를 계산 (기존 calculateMaxIds 로직)
   */
  calculateMaxIds({ commit }, { solarStrings = [] }) {
    console.log('🔍 최대 ID 계산 시작...');

    let maxModule = 0;
    let maxString = 0;

    // solarStrings가 없거나 빈 배열인 경우 기본값 반환
    if (!solarStrings || solarStrings.length === 0) {
      console.log('⚠️ solarStrings가 없어 기본값 반환');
      return { maxModuleId: maxModule, maxStringId: maxString };
    }

    // 서버에서 받아온 실제 데이터를 기반으로 최대 ID 계산
    solarStrings.forEach(solarString => {
      const stringId = parseInt(solarString.stringId);
      if (stringId > maxString) {
        maxString = stringId;
      }

      // 각 String의 모듈들에서 최대 Module ID 찾기
      if (solarString.solarModules) {
        solarString.solarModules.forEach(module => {
          // moduleId가 유효한 값인지 확인
          if (module.moduleId && !isNaN(parseInt(module.moduleId))) {
            const moduleId = parseInt(module.moduleId);
            if (moduleId > maxModule) {
              maxModule = moduleId;
            }
          }
        });
      }
    });

    console.log(`✅ 최대 ID 계산 완료 - Module: ${maxModule}, String: ${maxString}`);

    return { maxModuleId: maxModule, maxStringId: maxString };
  }
};

const getters = {
  // 🎯 서버 데이터 관련 getters (단일 진실 소스)
  getServerSiteDesignData: state => state.serverSiteDesignData,
  getSelectedSiteData: state => state.selectedSiteData,

  // 🎯 평면화된 데이터 — panelFlattenedRegistry
  getFlattenedData: () => getPanelFlattenedRegistry(),
  getVirtualTree: state => state.virtualTree,

  // 🎯 기존 파생 데이터 관련 getters
  getDerivedTreeData: () => getPanelTreeLocalRegistry(),
  getDerivedMapData: state => state.derivedMapData,
  getMapData: state => state.mapData,

  // 🎯 선택 상태 관련 getters
  getSelectedEntities: state => state.selectedEntities,
  getSelectedNodeCollection: state => state.selectedNodeCollection,
  isNodeSelected: state => nodeId => state.selectedNodeCollection.includes(nodeId),
  getEditingNodeId: state => state.editingNodeId,
  isDragging: state => state.isDraggingState,
  getDraggedNodeId: state => state.draggedNodeId,
  isEditing: state => state.isEditing,
  getPendingChanges: state => state.pendingChanges,

  // 🎯 동기화 상태 관련 getters
  getSynchronizationState: state => state.synchronizationState,
  isSynchronizing: state => state.synchronizationState.isSynchronizing,
  getSynchronizationPendingChanges: state => Array.from(state.synchronizationState.pendingChanges),
  getSynchronizationErrors: state => state.synchronizationState.synchronizationErrors,

  // 🎯 작업 상태 관련 getters
  isLoading: state => state.isLoadingState,
  isCreating: state => state.isCreatingState,
  isUpdating: state => state.isUpdatingState,
  isDeleting: state => state.isDeletingState,
  isBulkProcessing: state => state.isBulkProcessingState,

  // 🎯 에러/성공 상태 관련 getters
  getErrorState: state => state.errorState,
  getErrorMessage: state => state.errorMessage,
  getSuccessState: state => state.successState,
  getSuccessMessage: state => state.successMessage,

  // 🎯 계산된 getters
  hasSelectedNodes: state => state.selectedNodeCollection.length > 0,
  isAnyOperationInProgress: state =>
    state.isLoadingState ||
    state.isCreatingState ||
    state.isUpdatingState ||
    state.isDeletingState ||
    state.isBulkProcessingState,

  nextArrayName: (state, getters) => `Array ${getters.nextArrayNumber}`,

  nextArrayNumber: () =>
    resolveNextArrayDisplayId({
      flattened: getPanelFlattenedRegistry(),
      treeDisplayNumbers: collectPanelTreeArrayDisplayNumbers()
    }),

  /** Site — Array 표시 번호 최대값 (flattened 기준) */
  maxArrayDisplayIdSite: () =>
    calculateMaxArrayDisplayIdFromFlattened(getPanelFlattenedRegistry()),

  /** Array seq — String 표시 번호 최대값 */
  maxStringDisplayIdInArray: () => arraySeq =>
    resolveMaxStringDisplayIdInArray({
      flattened: getPanelFlattenedRegistry(),
      solarStrings: null,
      arraySeq
    }),

  /** Array seq — 다음 String 표시 번호 */
  nextStringDisplayIdInArray: () => arraySeq =>
    resolveNextStringDisplayIdInArray({
      flattened: getPanelFlattenedRegistry(),
      solarStrings: null,
      arraySeq
    }),

  /** String seq — Module 표시 번호 최대값 */
  maxModuleDisplayIdInString: () => stringSeq =>
    calculateMaxModuleIdInStringFromFlattened(getPanelFlattenedRegistry(), stringSeq),

  /** String seq — 다음 Module 표시 번호 */
  nextModuleDisplayIdInString: () => stringSeq =>
    resolveNextModuleDisplayIdInString({
      flattened: getPanelFlattenedRegistry(),
      stringSeq
    }),

  // Tree 구조 관련 getters (파생 데이터 기반)
  getArrayGroups: () => getPanelTreeLocalRegistry(),
  getArrayGroupById: () => arrayId =>
    getPanelTreeLocalRegistry().find(array => array.id === arrayId),

  getStringNodesByArrayId: () => arrayId => {
    const arrayGroup = getPanelTreeLocalRegistry().find(array => array.id === arrayId);
    return arrayGroup ? arrayGroup.children : [];
  },

  getModuleNodesByStringId: () => stringId => {
    for (const arrayGroup of getPanelTreeLocalRegistry()) {
      const stringNode = arrayGroup.children.find(string => string.id === stringId);
      if (stringNode) {
        return stringNode.children;
      }
    }
    return [];
  },

  // 지도 데이터 관련 getters (파생 데이터 기반)
  getMapFeatureById: state => featureId => state.derivedMapData.get(featureId),
  getMapFeaturesByType: state => type => {
    const features = [];
    state.derivedMapData.forEach(feature => {
      if (feature.type === type) {
        features.push(feature);
      }
    });
    return features;
  },

  // 🎯 낙관적 업데이트 관련 getters
  isOptimisticMode: state => state.optimisticUpdates.isOptimisticMode,
  getOptimisticPendingChanges: state =>
    Array.from(state.optimisticUpdates.pendingChanges.entries()),
  getOptimisticOriginalStates: state =>
    Array.from(state.optimisticUpdates.originalStates.entries()),
  hasOptimisticPendingChanges: state => state.optimisticUpdates.pendingChanges.size > 0,

  // 🎯 낙관적 업데이트가 적용된 데이터 getters (실제로는 derivedData와 동일)
  getOptimisticTreeData: () => getPanelTreeLocalRegistry(),
  getOptimisticMapData: state => state.derivedMapData
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
